import { AttachmentUploadRequestDTO, AttachmentUploadUrlDTO, AvailableContactDTO, ConversationDTO, ConversationMessageDTO, ConversationStatus, ConversationSummaryDTO, CreateConversationDTO, CursorDirection, CursorPageDTO, ExecuteActionResultDTO, MarkConversationReadDTO, MessageFeedbackResultDTO, RateMessageDTO, SendConversationMessageDTO, SupportHoursDTO } from 'models/conversations'
import { ConversationListCacheData, ConversationListPage, mapConversationListCache, nextConversationPageParam } from './conversationListCache'
import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'

import { _getUrl, useAPI } from 'utils/API'
import axios, { AxiosResponse } from 'axios'
import API from 'utils/API/API'
import { PageableResponse } from 'models/misc'
import { logger } from 'utils/logging'
import { lastMessageAttachmentFields } from 'components/common/ChatWidget/helpers/chatAttachments'
import { useAuth0 } from 'utils/auth'
import { useCallback } from 'react'
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'

enum Endpoints {
  LIST = '/conversations',
  CREATE = '/conversations',
  GET_ONE = '/conversations/{conversationId}',
  UPDATE = '/conversations/{conversationId}',
  AVAILABLE_CONTACTS = '/user/contacts/available',
  SEND_MESSAGE = '/conversations/{conversationId}/messages',
  LIST_MESSAGES = '/conversations/{conversationId}/messages',
  MARK_READ = '/conversations/{conversationId}/messages/read',
  EXECUTE_ACTION = '/conversations/{conversationId}/messages/{messageId}/actions/{actionId}',
  MESSAGE_FEEDBACK = '/conversations/{conversationId}/messages/{messageId}/feedback',
  SUPPORT_HOURS = '/conversations/support-hours',
  ATTACHMENT_UPLOAD_URLS = '/conversations/{conversationId}/attachments/upload-urls',
}

/** Custom query keys for the conversation domain. */
export enum ConversationQueryKeys {
  AVAILABLE_CONTACTS = 'AVAILABLE_CONTACTS',
  SUPPORT_HOURS = 'SUPPORT_HOURS',
}

/** Base React Query key for the conversation list — used by the real-time layer to patch the cache. */
export const conversationsListKey = (status?: ConversationStatus) =>
  [EntityKeys.CONVERSATION, QueryType.LIST, { status: status ?? null }] as const

/** Fetch the authenticated user's conversations, optionally filtered by status (sorted most-recent first). */
export function useGetConversations(status?: ConversationStatus) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: conversationsListKey(status),
    queryFn: () => api.get<PageableResponse<ConversationSummaryDTO[]>>(
      Endpoints.LIST,
      {},
      false,
      {
        params: {
          page: 0,
          size: 100,
          ...(status ? { status } : {}),
        },
      }
    ),
    // Robustness fallback: the real-time path notifies a participant of a brand-new conversation on
    // their personal WS topic, but that subscription needs the caller's resolved id (missing for a
    // creative with no prior conversation). Polling guarantees a newly-created conversation surfaces
    // (and gets its topic subscribed) within the interval even when that WS notification is missed.
    refetchInterval: CONVERSATIONS_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  })
}

const CONVERSATIONS_REFETCH_INTERVAL_MS = 15_000

export const CONVERSATION_PAGE_SIZE = 20

export const closedConversationPagesKey = () =>
  [EntityKeys.CONVERSATION, QueryType.LIST, { status: ConversationStatus.CLOSED, paged: true }] as const

export function useGetClosedConversationPages() {
  const api = useAPI<Endpoints>()

  return useInfiniteQuery({
    queryKey: closedConversationPagesKey(),
    queryFn: ({ pageParam }) => api.get<ConversationListPage>(
      Endpoints.LIST,
      {},
      false,
      {
        params: { page: pageParam, size: CONVERSATION_PAGE_SIZE, status: ConversationStatus.CLOSED },
      }
    ),
    initialPageParam: 0,
    getNextPageParam: nextConversationPageParam,
  })
}

export const CONVERSATION_SEARCH_MIN_LENGTH = 2

export const CONVERSATION_SEARCH_MAX_LENGTH = 100

export const isServerSearchable = (query: string) =>
  query.length >= CONVERSATION_SEARCH_MIN_LENGTH && query.length <= CONVERSATION_SEARCH_MAX_LENGTH

export const conversationsSearchKey = (query: string) =>
  [EntityKeys.CONVERSATION, QueryType.LIST, { search: query }] as const

export function useSearchConversations(query: string) {
  const api = useAPI<Endpoints>()
  const trimmed = query.trim()

  return useInfiniteQuery({
    queryKey: conversationsSearchKey(trimmed),
    queryFn: ({ pageParam }) => api.get<ConversationListPage>(
      Endpoints.LIST,
      {},
      false,
      {
        params: { page: pageParam, size: CONVERSATION_PAGE_SIZE, q: trimmed },
      }
    ),
    initialPageParam: 0,
    getNextPageParam: nextConversationPageParam,
    enabled: isServerSearchable(trimmed),
    staleTime: CONVERSATIONS_REFETCH_INTERVAL_MS,
  })
}

/** Base React Query key for a conversation's message page — exposed so the real-time layer can patch it. */
export const conversationMessagesKey = (conversationId: string) =>
  [EntityKeys.CONVERSATION, QueryType.GET_ONE, conversationId, 'messages'] as const

/** Fetch the last page of messages for a conversation (oldest→newest, descending from now). */
export function useGetMessages(conversationId: string | null | undefined, limit = 50) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: conversationId ? conversationMessagesKey(conversationId) : ['conversation-messages-disabled'],
    queryFn: () => api.get<CursorPageDTO<ConversationMessageDTO>>(
      Endpoints.LIST_MESSAGES,
      { conversationId: conversationId as string },
      false,
      {
        params: { limit, direction: CursorDirection.BEFORE },
      }
    ),
    enabled: Boolean(conversationId),
  })
}

/**
 * Returns a function to fetch a page of messages strictly older than `beforeMessageId`
 * (cursor pagination, `direction=BEFORE`). Used for infinite scroll into history — kept outside the
 * `useGetMessages` cache so the real-time tail page and its patchers stay untouched.
 */
export function useFetchOlderMessages(limit = 50) {
  const api = useAPI<Endpoints>()

  return useCallback(
    (conversationId: string, beforeMessageId: string) =>
      api.get<CursorPageDTO<ConversationMessageDTO>>(
        Endpoints.LIST_MESSAGES,
        { conversationId },
        false,
        {
          params: { limit, direction: CursorDirection.BEFORE, cursor: beforeMessageId },
        }
      ),
    [api, limit]
  )
}

export function useFetchMessagesPage(limit = 50) {
  const api = useAPI<Endpoints>()

  return useCallback(
    (conversationId: string, beforeMessageId?: string) =>
      api.get<CursorPageDTO<ConversationMessageDTO>>(
        Endpoints.LIST_MESSAGES,
        { conversationId },
        false,
        {
          params: { limit, direction: CursorDirection.BEFORE, ...(beforeMessageId && { cursor: beforeMessageId }) },
        }
      ),
    [api, limit]
  )
}

/**
 * Returns a function that fetches one conversation straight from the server, bypassing the cached list: a
 * tab's list is only as fresh as its last poll and holds the first page only.
 */
export function useFetchConversation() {
  const { roles } = useAuth0()

  return useCallback(
    (conversationId: string) => API.get<ConversationDTO>(
      _getUrl(Endpoints.GET_ONE, { conversationId }),
      { headers: { Accept: API.decideRoleMimeType(roles) } },
      { endpoint: Endpoints.GET_ONE, ignoreErrorFactory: isGoneError }
    ).then((response) => response.data),
    [roles]
  )
}

export const isGoneError = (error: unknown) =>
  axios.isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 403)

export const isConversationClosedError = (error: unknown) => {
  if (!axios.isAxiosError(error) || error.response?.status !== 403) return false
  const message = (error.response.data as { message?: unknown } | undefined)?.message
  return typeof message === 'string' && message.includes('CLOSED conversation')
}

export const responseStatusOf = (error: unknown): number | null =>
  axios.isAxiosError(error) ? error.response?.status ?? null : null

const RATING_CONFLICT_STATUS = 409
const RATING_EFFECT_REFUSED_STATUS = 422
const RATING_RETRY_TOO_SOON_STATUS = 429
const RATING_HANDLED_STATUSES = new Set([RATING_CONFLICT_STATUS, RATING_EFFECT_REFUSED_STATUS, RATING_RETRY_TOO_SOON_STATUS])

export const isRatingConflict = (error: unknown) => responseStatusOf(error) === RATING_CONFLICT_STATUS

export const isRatingEffectRefused = (error: unknown) => responseStatusOf(error) === RATING_EFFECT_REFUSED_STATUS

export const isRatingRetryTooSoon = (error: unknown) => responseStatusOf(error) === RATING_RETRY_TOO_SOON_STATUS

export const isRatingRejectedAsClosed = (error: unknown) => {
  if (!axios.isAxiosError(error) || error.response?.status !== RATING_CONFLICT_STATUS) return false
  const message = (error.response.data as { message?: unknown } | undefined)?.message
  return typeof message === 'string' && /conversation is closed/i.test(message)
}

export const supportHoursKey = [EntityKeys.CONVERSATION, QueryType.GET, ConversationQueryKeys.SUPPORT_HOURS] as const

export const SUPPORT_HOURS_REFRESH_MS = 5 * 60_000

export function useGetSupportHours(enabled = true) {
  const { roles } = useAuth0()
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: supportHoursKey,
    queryFn: async () => {
      try {
        const response = await API.get<SupportHoursDTO>(
          _getUrl(Endpoints.SUPPORT_HOURS, {}),
          { headers: { Accept: API.decideRoleMimeType(roles) } },
          { endpoint: Endpoints.SUPPORT_HOURS, ignoreError: true }
        )
        return response.data
      } catch (error) {
        const status = axios.isAxiosError(error) ? error.response?.status ?? null : null
        const previous = queryClient.getQueryState(supportHoursKey)
        const isOngoingOutage = !!previous && previous.errorUpdatedAt > previous.dataUpdatedAt
        if (status !== 401 && !isOngoingOutage) {
          const code = axios.isAxiosError(error) ? error.code ?? null : null
          const message = error instanceof Error ? error.message : null
          logger.warn('ChatWidget: support hours unavailable, hiding the hours UI', undefined, {
            context: { component: 'useGetSupportHours', action: 'fetchSupportHours', metadata: { status, code, message } },
          })
        }
        throw error
      }
    },
    enabled,
    staleTime: SUPPORT_HOURS_REFRESH_MS,
    refetchInterval: SUPPORT_HOURS_REFRESH_MS,
    refetchOnWindowFocus: true,
    retry: false,
    retryOnMount: false,
  })
}

/**
 * Returns a function that primes the React Query cache with the head page of a conversation's
 * messages. Used by the chat widget to load history eagerly the moment the conversation list
 * lands, so opening a thread doesn't show a spinner.
 */
export function usePrefetchMessages(limit = 50) {
  const api = useAPI<Endpoints>()
  const queryClient = useQueryClient()

  return useCallback((conversationId: string) => {
    queryClient.prefetchQuery({
      queryKey: conversationMessagesKey(conversationId),
      queryFn: () => api.get<CursorPageDTO<ConversationMessageDTO>>(
        Endpoints.LIST_MESSAGES,
        { conversationId },
        false,
        {
          params: { limit, direction: CursorDirection.BEFORE },
        }
      ),
    })
  }, [api, limit, queryClient])
}

/**
 * Fetch the contacts the current user can start a conversation with.
 *
 * TODO: no callers while the guided Client↔Creative flow is parked (ISSUE-2345 made the Ops support
 * conversation the widget's only entry). Kept for the phase that re-enables an explicit creative picker —
 * drop it, and `GET /user/contacts/available` with it, if that phase does not land.
 */
export function useGetAvailableContacts(enabled = true) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: [EntityKeys.CONVERSATION, QueryType.LIST, ConversationQueryKeys.AVAILABLE_CONTACTS],
    queryFn: () => api.get<AvailableContactDTO[]>(
      Endpoints.AVAILABLE_CONTACTS,
      {},
      false
    ),
    enabled,
  })
}

/** Create a new conversation and refresh the conversation list. */
export function useCreateConversation() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<ConversationDTO>, CreateConversationDTO>(
    (payload) => api.post(
      Endpoints.CREATE,
      {},
      payload,
      true
    ),
    (client) => {
      client.invalidateQueries({
        queryKey: [EntityKeys.CONVERSATION, QueryType.LIST],
      })
    }
  )
}

/** Send a message in a conversation via the REST fallback endpoint. */
export type SendMessagePayload = SendConversationMessageDTO & { conversationId: string }

export function useSendMessage() {
  const { roles } = useAuth0()

  return getMutation<AxiosResponse<ConversationMessageDTO>, SendMessagePayload>(
    ({ conversationId, ...body }) => API.post<ConversationMessageDTO>(
      _getUrl(Endpoints.SEND_MESSAGE, { conversationId }),
      body,
      { headers: { Accept: API.decideRoleMimeType(roles) } },
      { endpoint: Endpoints.SEND_MESSAGE, ignoreErrorFactory: isConversationClosedError }
    ),
    (client, variables, response) => {
      const message: ConversationMessageDTO = response.data

      // GET /conversations does not (yet) include `lastMessagePreview` — patch every cached
      // conversation-list query so the item shows the message we just sent without waiting for
      // a backend roundtrip. TODO: drop once the backend list endpoint enriches the response.
      client.setQueriesData<ConversationListCacheData>(
        { queryKey: [EntityKeys.CONVERSATION, QueryType.LIST] },
        (cached) => mapConversationListCache(cached, (summaries) => {
          const index = summaries.findIndex((c) => c.id === variables.conversationId)
          if (index === -1) return summaries
          const updated = {
            ...summaries[index],
            lastMessagePreview: message.content,
            ...lastMessageAttachmentFields(message.attachments),
            lastMessageAt: message.createdAt,
            lastMessageSenderId: message.senderId,
            lastMessageSource: message.source,
            lastMessageSenderKind: undefined,
          }
          const content = [...summaries]
          content.splice(index, 1)
          content.unshift(updated)
          return content
        })
      )

      // Reconcile the cached thread: replace the optimistic bubble (same clientMessageId) with the
      // server's persisted message (real id, delivery status), or append if it isn't there yet. The
      // WebSocket broadcast later upserts again idempotently (same clientMessageId/id).
      client.setQueryData(
        conversationMessagesKey(variables.conversationId),
        (cached: CursorPageDTO<ConversationMessageDTO> | undefined) => {
          if (!cached) return cached
          const index = cached.items.findIndex(
            (m) => m.id === message.id || m.clientMessageId === message.clientMessageId
          )
          if (index >= 0) {
            const items = [...cached.items]
            items[index] = { ...items[index], ...message }
            return { ...cached, items, size: items.length }
          }
          return { ...cached, items: [...cached.items, message], size: cached.items.length + 1 }
        }
      )
    }
  )
}

export function useRequestAttachmentUploadUrls() {
  const api = useAPI<Endpoints>()

  return useCallback(
    (conversationId: string, files: AttachmentUploadRequestDTO[]) =>
      api.post<AttachmentUploadUrlDTO[]>(
        Endpoints.ATTACHMENT_UPLOAD_URLS,
        { conversationId },
        files,
        false
      ),
    [api]
  )
}

/** Runs a suggestion. Only its `value` is sent: what it acts on is read server-side from the message. */
export type ExecuteActionPayload = { conversationId: string; messageId: string; actionId: string }

export function useExecuteMessageAction() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<ExecuteActionResultDTO>, ExecuteActionPayload>(
    ({ conversationId, messageId, actionId }) => api.post(
      Endpoints.EXECUTE_ACTION,
      { conversationId, messageId, actionId },
      {},
      true
    ),
    (client, variables) => {
      // A creative route creates or reuses a conversation, so the list is stale either way.
      void client.invalidateQueries({ queryKey: [EntityKeys.CONVERSATION, QueryType.LIST] })
      // Picks up the question the action posted, not the assistant's answer — that one still rests on a
      // single socket broadcast.
      void client.invalidateQueries({ queryKey: conversationMessagesKey(variables.conversationId) })
    }
  )
}

export type RateMessagePayload = RateMessageDTO & { conversationId: string; messageId: string }

export function useRateAssistantAnswer() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<MessageFeedbackResultDTO>, RateMessagePayload>(
    ({ conversationId, messageId, rating, confirmed }) => api.put<MessageFeedbackResultDTO>(
      Endpoints.MESSAGE_FEEDBACK,
      { conversationId, messageId },
      { rating, confirmed },
      true,
      undefined,
      { ignoreErrorFactory: (error) => RATING_HANDLED_STATUSES.has(error.response?.status ?? -1) }
    ),
    (client, variables, response: AxiosResponse<MessageFeedbackResultDTO>) => {
      client.setQueryData(
        conversationMessagesKey(variables.conversationId),
        (cached: CursorPageDTO<ConversationMessageDTO> | undefined) => {
          if (!cached?.items.some((message) => message.id === variables.messageId)) return cached
          return {
            ...cached,
            items: cached.items.map((message) => message.id === variables.messageId ? { ...message, feedback: response.data.rating } : message),
          }
        }
      )
      void client.invalidateQueries({ queryKey: [EntityKeys.CONVERSATION, QueryType.LIST] })
    }
  )
}

/** Update a conversation's status (the backend only accepts a CLOSED transition) and refresh the list. */
export function useUpdateConversationStatus() {
  const api = useAPI<Endpoints>()

  return getMutation<void, { conversationId: string; status: ConversationStatus }>(
    ({ conversationId, status }) => api.patch(Endpoints.UPDATE, { conversationId }, { status }, false),
    (client) => {
      void client.invalidateQueries({ queryKey: [EntityKeys.CONVERSATION, QueryType.LIST] })
    }
  )
}

/** Mark the caller's read cursor at `lastReadMessageId` (resets `unreadCount` to 0 in the list). */
export type MarkReadPayload = MarkConversationReadDTO & { conversationId: string }

export function useMarkConversationRead() {
  const api = useAPI<Endpoints>()

  return getMutation<void, MarkReadPayload>(
    ({ conversationId, ...body }) => api.put(
      Endpoints.MARK_READ,
      { conversationId },
      body,
      false
    ),
    (client, variables) => {
      const listFilter = { queryKey: [EntityKeys.CONVERSATION, QueryType.LIST] }
      // Zero out the unread badge locally; the server will broadcast a read receipt to other
      // participants which our WS handler patches into their thread.
      const isListFetching = client.isFetching(listFilter) > 0
      client.setQueriesData<ConversationListCacheData>(
        listFilter,
        (cached) => mapConversationListCache(cached, (summaries) =>
          summaries.some((c) => c.id === variables.conversationId && c.unreadCount !== 0)
            ? summaries.map((c) => (c.id === variables.conversationId ? { ...c, unreadCount: 0 } : c))
            : summaries
        )
      )
      if (isListFetching) void client.invalidateQueries(listFilter)
    }
  )
}
